import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container, Box, Paper, Stack, Typography, Button, Chip, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  LinearProgress, Fade, Grow, alpha, useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import DashboardIcon from '@mui/icons-material/Dashboard';

import { useApi } from '../../hooks/useApi';

function Index() {
  const jApi = useApi();
  const theme = useTheme();
  const navigate = useNavigate();

  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const response = await jApi.request('GET', 'cma/qbrprogram');
      const list = Array.isArray(response.data) ? response.data : [];
      list.sort((a, b) => (a.customer || '').localeCompare(b.customer || ''));
      setPrograms(list);
    } catch (ex) {
      setErrorMessage(ex?.toString?.() || 'Failed to load QBR programs');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    p: 2,
    position: 'relative',
    overflow: 'hidden',
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="xl">
        {/* Breadcrumb */}
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}
              sx={{ display: 'inline-flex', px: 2, py: 1, borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <HomeRoundedIcon sx={{ fontSize: 18, color: theme.palette.secondary.main, mr: 0.5 }}/>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>Home</Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>QBR</Typography>
            </Stack>
          </Box>
        </Fade>

        {/* Header */}
        <Grow in timeout={700}>
          <Box sx={headerGradient}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  QBR Programs
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isLoading ? 'Loading…' : `${programs.length} customer${programs.length === 1 ? '' : 's'} enrolled`}
                </Typography>
              </Box>
              <IconButton size="small" onClick={fetchData} disabled={isLoading}>
                <RefreshIcon/>
              </IconButton>
              <Button component={RouterLink} to="/qbr/calendar" variant="outlined" size="small" startIcon={<EventIcon/>}>
                Calendar
              </Button>
              <Button component={RouterLink} to="/qbr/hub" variant="outlined" size="small" startIcon={<DashboardIcon/>}>
                Hub
              </Button>
              <Button component={RouterLink} to="/qbr/program/new" variant="contained" size="small" startIcon={<AddIcon/>}>
                Enroll
              </Button>
            </Stack>
            {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}/>}
          </Box>
        </Grow>

        {errorMessage && (
          <Paper sx={{ p: 2, mt: 2, background: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}` }}>
            <Typography variant="body2" color="error.main">{errorMessage}</Typography>
          </Paper>
        )}

        {!isLoading && programs.length === 0 && (
          <Paper sx={{ p: 4, mt: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No customers enrolled yet. Click <strong>Enroll</strong> to add one.
            </Typography>
          </Paper>
        )}

        {!isLoading && programs.length > 0 && (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Account Manager</TableCell>
                  <TableCell>vCIO</TableCell>
                  <TableCell>Target Date</TableCell>
                  <TableCell align="right">Cadence</TableCell>
                  <TableCell align="center">Active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {programs.map((p) => (
                  <TableRow key={p.id} hover sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/qbr/program/${p.id}`)}>
                    <TableCell sx={{ fontWeight: 600 }}>{p.customer}</TableCell>
                    <TableCell><code>{p.account}</code></TableCell>
                    <TableCell>{p.account_manager || '—'}</TableCell>
                    <TableCell>{p.vcio || '—'}</TableCell>
                    <TableCell>{p.target_date || '—'}</TableCell>
                    <TableCell align="right">{p.cadence_months || 3} mo</TableCell>
                    <TableCell align="center">
                      <Chip size="small"
                        label={p.active ? 'Active' : 'Inactive'}
                        color={p.active ? 'success' : 'default'}/>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Box>
  );
}

export default Index;
